import { useState } from "react";
import { ArrowIcon } from "./ArrowIcon";
import { GOALS, MONTHS, QUESTIONS, formatMoney } from "./constants";
import { WorthAnswer } from "./WorthAnswer";
import { SpendingAnswer } from "./SpendingAnswer";
import { ProgressAnswer } from "./ProgressAnswer";
import type { QuestionId } from "./types";
import { getSpendingComparison } from "./utils";
import {
  Answer,
  AnswerPanels,
  AnswerPanel,
  Explorer,
  QuestionButton,
  QuestionCopy,
  QuestionList,
  QuestionRail,
  SampleCaption,
  SrOnly,
} from "./styles";

export function QuestionsExplorer() {
  const [question, setQuestion] = useState<QuestionId>("worth");
  const [selectedMonth, setSelectedMonth] = useState(2);
  const [selectedGoal, setSelectedGoal] = useState(0);
  const active = QUESTIONS.find(({ id }) => id === question) ?? QUESTIONS[0];
  const month = MONTHS[selectedMonth];
  const goal = GOALS[selectedGoal];
  const isWorth = question === "worth";
  const isSpending = question === "spending";
  const isProgress = question === "progress";
  let announcement =
    "Net worth: €57,240. Assets: €69,000. Liabilities: €11,760.";
  if (isSpending) {
    announcement = `${month.name}: ${formatMoney(month.income - month.expenses)} left after expenses. ${getSpendingComparison(selectedMonth)}`;
  } else if (isProgress) {
    announcement = `${goal.name}: ${formatMoney(goal.saved)} saved of ${formatMoney(goal.target)}.`;
  }
  const answerTitleId = `${question}-answer-title`;
  const questionButtons = QUESTIONS.map(({ id, label }) => {
    const isSelected = question === id;
    return (
      <QuestionButton
        type="button"
        key={id}
        aria-pressed={isSelected}
        aria-controls="question-answer"
        onClick={() => setQuestion(id)}
      >
        <span>{label}</span>
        <ArrowIcon />
      </QuestionButton>
    );
  });
  return (
    <Explorer
      id="question-explorer"
      aria-label="Explore three financial questions"
    >
      <QuestionRail>
        <QuestionList role="group" aria-label="Choose a financial question">
          {questionButtons}
        </QuestionList>
        <QuestionCopy>
          <h2>{active.explanation}</h2>
          <p>{active.detail}</p>
        </QuestionCopy>
      </QuestionRail>
      <Answer id="question-answer" aria-labelledby={answerTitleId}>
        <SampleCaption>
          <span>Explore a sample</span>
          <span>30 Oct 2026 · EUR</span>
        </SampleCaption>
        <AnswerPanels>
          <AnswerPanel
            $active={isWorth}
            aria-hidden={!isWorth}
            inert={!isWorth}
          >
            <WorthAnswer />
          </AnswerPanel>
          <AnswerPanel
            $active={isSpending}
            aria-hidden={!isSpending}
            inert={!isSpending}
          >
            <SpendingAnswer
              month={month}
              selectedMonth={selectedMonth}
              onMonthChange={setSelectedMonth}
            />
          </AnswerPanel>
          <AnswerPanel
            $active={isProgress}
            aria-hidden={!isProgress}
            inert={!isProgress}
          >
            <ProgressAnswer
              goal={goal}
              selectedGoal={selectedGoal}
              onGoalChange={setSelectedGoal}
            />
          </AnswerPanel>
        </AnswerPanels>
      </Answer>
      <SrOnly role="status" aria-live="polite" aria-atomic="true">
        {active.label} {announcement}
      </SrOnly>
    </Explorer>
  );
}
